import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import { HttpService } from "../../services/httpService";
import { getClient } from "../../services/clientService";
import { getOrderFormByOrderIDType } from "../../services/orderFormService";
import { NotificationType } from "../../interfaces/clientNotification";
import { getPdfBase64 } from "../../services/helpers";
import { sendNotifyClientEmail } from "../../services/emailService";

interface NotifyClientProps {
  orderId: number;
  clientId: number;
  detailId?: number;
  candidateEmail?: string;
  type: NotificationType;
  formName?: string;
  onClose: (message?: string) => void;
}

const NotifyClient: React.FC<NotifyClientProps> = ({
  orderId,
  detailId,
  clientId,
  candidateEmail,
  type,
  formName,
  onClose,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [client, setClient] = useState<Client | null>(null);

  useEffect(() => {
    const fetchPdfUrl = async () => {
      const clientData = await getClient(clientId);
      setClient(clientData);

      try {
        setLoadingSpinner(true);
        let url = null;

        if (type === NotificationType.Reference && detailId && formName) {
          url = await HttpService.getFileAdmin(
            "reference-detail-forms",
            detailId.toString(),
            formName,
          );
        } else if (type === NotificationType.Criminal && detailId && formName) {
          url = await HttpService.getFileAdmin(
            "criminal-detail-forms",
            detailId.toString(),
            formName,
          );
        } else if (type === NotificationType.Candidate) {
          const formResponse = await getOrderFormByOrderIDType(orderId, 6);
          if (formResponse?.completedFormUrl) {
            url = await HttpService.getFileAdmin(
              "candidate-screening-questionnaires",
              formResponse.orderFormID.toString(),
              formResponse.completedFormUrl,
            );
          }
        }

        setPdfUrl(url);
      } catch (error) {
        setErrorMessage("Error fetching report URL.");
      } finally {
        setLoadingSpinner(false);
      }
    };

    fetchPdfUrl();
  }, [detailId, type, formName, orderId, clientId]);

  const handleNotify = async () => {
    setIsSending(true);
    setLoadingSpinner(true);
    setErrorMessage(null);

    try {
      let base64 = undefined;
      if (pdfUrl) {
        base64 = await getPdfBase64(pdfUrl);
      }

      const result = await sendNotifyClientEmail({
        orderId,
        type,
        detailId,
        message: message ?? "",
        signedResponseBase64: base64,
        candidateEmail,
      });

      if (result.success) {
        onClose("Notified successfully.");
      } else {
        setErrorMessage(result.error ?? "");
      }
    } catch (error) {
      setErrorMessage("Failed to send notification.");
    } finally {
      setIsSending(false);
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          {type === NotificationType.NotifyCandidate ? (
            <h2>Notify Candidate</h2>
          ) : (
            <h2>Notify Client</h2>
          )}

          <div>
            {type === NotificationType.Candidate
              ? "Email(s) will be sent notifying the client that a candidate questionnaire has been received that contains red flags."
              : type === NotificationType.Reference
                ? "Email(s) will be sent notifying the client that a reference response has been received that contains red flags."
                : type === NotificationType.Criminal
                  ? "Email(s) will be sent notifying the client that a criminal report has been returned that contains red flags."
                  : type === NotificationType.NotifyCandidate
                    ? "Email will be sent notifying the candidate that not all of their references have responded yet. "
                    : type === NotificationType.Subscription
                      ? "Email will be sent notifying the client that a subscription alert is ready for review."
                      : "Email(s) will be sent notifying the client that the candidate has completed all forms and the background check process will begin."}
          </div>
        </div>
        <br></br>
        <div className="dialog-container">
          {type === NotificationType.NotifyCandidate ? (
            <div className="field-long" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Candidate Email:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={candidateEmail}
                  disabled
                />
              </div>
            </div>
          ) : (
            <div className="field-long" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Client Name:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={client?.clientName ? client?.clientName : ""}
                  disabled
                />
              </div>
            </div>
          )}

          {type === NotificationType.Criminal && (
            <div className="field-long" style={{ flex: "0 0 45%" }}>
              <label className="field-label">Type:</label>
              <div className="field-input">
                <select
                  className="form-control"
                  value={message ?? ""}
                  onChange={(e) => setMessage(e.target.value)}
                >
                  <option value="">Select an option</option>
                  <option value="Credit">Credit</option>
                  <option value="Motor">Motor</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          )}

          {type !== NotificationType.Order &&
            type !== NotificationType.NotifyCandidate &&
            type !== NotificationType.Subscription && (
              <div className="field-long" style={{ flex: "0 0 100%" }}>
                <label className="field-label">
                  {type === NotificationType.Reference
                    ? "Reference Response:"
                    : type === NotificationType.Criminal
                      ? "Vendor Response:"
                      : "Candidate Response"}
                </label>
                <div className="field-input">
                  {pdfUrl && (
                    <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                      View Report{" "}
                      <i className="btn-icon bi bi-box-arrow-up-right"></i>
                    </a>
                  )}
                </div>
              </div>
            )}
          {errorMessage && <div className="error">{errorMessage}</div>}
        </div>
        <div className="filter-actions">
          <button
            className={`smbtn btn-green`}
            onClick={handleNotify}
            disabled={isSending}
          >
            {type === NotificationType.NotifyCandidate
              ? "Notify Candidate"
              : "Notify Client"}
          </button>
        </div>
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default NotifyClient;
